import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient as queryClientShared } from "@/lib/queryClient";
import { useRouter, Stack, useSegments } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import * as Notifications from 'expo-notifications';
import { useEffect, useRef, useState } from "react";
import { StyleSheet, View, Modal, TouchableOpacity, TextInput, ActivityIndicator, KeyboardAvoidingView, Platform, Alert } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider, KeyboardAvoidingView as KBAvoidingView } from "react-native-keyboard-controller";
import colors from "@/constants/colors";
import { usePostSignInInvites } from "@/hooks/usePostSignInInvites";

// i18n — must be imported before any component renders
import '../i18n';

// Context Imports
import { AuthContext, useAuth } from '@/context/AuthContext';
import { DogsContext, useDogs } from '@/context/DogsContext';
import { SelectedDogContext } from '@/context/SelectedDogContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import LottieLoader from '@/components/LottieLoader';
import { Text } from '@/components';
import { MessageSquareQuote } from 'lucide-react-native';
import { API_URL } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';
import { initLifecycleTracking, logEvent } from '@/lib/analytics';
import { initWsLifecycle } from '@/lib/ws';
import { initTrailHealthMonitor } from '@/utils/trailHealth';
import { notificationToRoute } from '@/lib/deepLinks';

// 1. Prevent the splash screen from auto-hiding immediately.
// This must be called at the top level, outside any component.
SplashScreen.preventAutoHideAsync().catch(() => {
  /* Reloading in development sometimes triggers a rejection here; we safely ignore it */
});

const queryClient = queryClientShared;

// Notification handler is configured in hooks/usePushNotifications.ts

function RootLayoutNav() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, session } = useAuth();
  const { hasDogProfile, isDogProfileLoading, dogProfileError, refreshDogProfile } = useDogs();
  const segments = useSegments();
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Apply persisted analytics consent (enable/disable Firebase collection) on launch.
  // ponytail: default-on for testing — see hooks/useAnalyticsConsent.ts PRODUCTION GATE.
  useAnalyticsConsent();
  // app_open / app_background lifecycle events for the internal analytics DB
  // (MAU/WAU/retention/session-length). Respects the same consent toggle.
  initLifecycleTracking();
  // group chat websocket: connect while authed+foregrounded, drop on background
  initWsLifecycle(() => session?.accessToken ?? null);
  // trail-health watchdog: local notification if a walk stops being recorded mid-way
  initTrailHealthMonitor();
  usePostSignInInvites();

  // Register for push notifications (extracted from NotificationsContext)
  usePushNotifications();

  // ─── Notification deep-link handler ────────────────────────────────
  // When a user taps a push notification, navigate to the relevant screen.
  // If auth/dog profile hasn't resolved yet, queue the deep link and navigate
  // once the app is ready.
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);
  const coldStartHandled = useRef(false);

  useEffect(() => {
    // expo-notifications' tap APIs (response listener + last-notification
    // response) are native-only on web — skip entirely, taps don't exist there.
    if (Platform.OS === 'web') return;
    const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
      const data = response.notification.request.content.data;
      // push effectiveness: which notification types actually get tapped
      logEvent('push_opened', { type: String(data?.type ?? 'unknown') });
      const route = notificationToRoute(data);
      if (route) {
        if (isReady && isAuthenticated && hasDogProfile) {
          router.push(route as any);
        } else {
          // Queue for later — navigate once auth/dog profile resolves
          setPendingDeepLink(route);
        }
      }
    });

    // cold start (app killed → push tap): the payload that launched the app.
    // Read once — getLastNotificationResponse keeps returning the same launch
    // payload, and re-queueing it would re-push the screen after navigation.
    if (!coldStartHandled.current) {
      coldStartHandled.current = true;
      const initial = Notifications.getLastNotificationResponse();
      if (initial) {
        const route = notificationToRoute(initial.notification.request.content.data);
        if (route) setPendingDeepLink(route);
      }
    }
    return () => subscription.remove();
  }, [router, isReady, isAuthenticated, hasDogProfile]);

  // Navigate to queued deep link once the app is ready
  useEffect(() => {
    if (pendingDeepLink && isReady && isAuthenticated && hasDogProfile && !isDogProfileLoading) {
      router.push(pendingDeepLink as any);
      setPendingDeepLink(null);
    }
  }, [pendingDeepLink, isReady, isAuthenticated, hasDogProfile, isDogProfileLoading, router]);

  // Hide FAB on certain pages that have critical UI interactions
  // const hideFab = segments.includes('record') || segments.includes('follow') || segments.includes('login') || segments.includes('signup') || segments.includes('onboarding') || segments.includes('summary');
  const hideFab = true;
  async function submitFeedback(message: string) {
    setIsSubmittingFeedback(true);
    try {
      const headers: Record<string, string> = {};
      if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;

      const res = await fetch(`${API_URL}/feedback?message=${encodeURIComponent(message)}`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to send feedback');
      logEvent('feedback_submitted', { length: message.length });
      setFeedbackText('');
      setIsFeedbackOpen(false);
    } catch (err) {
      console.error('Feedback error', err);
      Alert.alert(t('common.error'), t('feedback.failedToSend'));
    } finally {
      setIsSubmittingFeedback(false);
    }
  }

  // 2. SYNC SPLASH SCREEN HIDING
  // We only hide the splash once the AUTH and DOG data are settled.
  useEffect(() => {
    if (!isLoading && !isDogProfileLoading) {
      // Small delay ensures the first frame of the app is rendered 
      // before the splash screen is pulled away on Android.
      const timer = setTimeout(async () => {
        await SplashScreen.hideAsync().catch(() => { });
        setIsReady(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isDogProfileLoading]);

  // 3. NAVIGATION LOGIC
  useEffect(() => {
    // Only run navigation logic once we aren't loading and the UI is ready
    if (isLoading || isDogProfileLoading || !isReady) return;

    const inAuthGroup = segments[0] === '(tabs)';
    const inLoginOrSignup = segments[0] === 'login' || segments[0] === 'signup';
    const inOnboarding = segments[0] === 'onboarding';

    if (!isAuthenticated && (inAuthGroup || inOnboarding)) {
      router.replace('/login');
    } else if (isAuthenticated && !hasDogProfile && !dogProfileError && !inOnboarding) {
      router.replace('/onboarding/dog-profile');
    } else if (isAuthenticated && dogProfileError && !inOnboarding) {
      // API error loading dog profile — don't redirect to onboarding.
      // User will see the retry screen rendered below.
    } else if (isAuthenticated && hasDogProfile && inLoginOrSignup) {
      router.replace('/(tabs)/explore');
    }
  }, [isAuthenticated, hasDogProfile, segments, isLoading, isDogProfileLoading, isReady]);

  // 1. If we are still determining auth or dog status, show loader
  if (isLoading || isDogProfileLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.backgroundPrimary }}>
        <LottieLoader />
      </View>
    );
  }

  // 2. If dog profile API failed, show a retry screen (not onboarding)
  if (isAuthenticated && dogProfileError) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.backgroundPrimary }}>
        <Text style={{ fontSize: 18, fontWeight: '600', color: colors.textPrimary, marginBottom: 8 }}>
          {t('common.error')}
        </Text>
        <Text style={{ fontSize: 14, color: colors.textSecondary, marginBottom: 24, textAlign: 'center', paddingHorizontal: 32 }}>
          {t('errors.dogProfileLoadFailed')}
        </Text>
        <TouchableOpacity
          style={{ backgroundColor: colors.accentPrimary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8 }}
          onPress={refreshDogProfile}
        >
          <Text style={{ color: colors.backgroundPrimary, fontWeight: '600' }}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // 2. If the splash screen hasn't finished hiding, don't mount the Stack yet
  // This prevents the "stale" error during the quick transition from 
  // "Logged Out" -> "Logged In"
  if (!isReady) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.backgroundPrimary }}>
        <LottieLoader />
      </View>
    );
  }

  return (
    <KBAvoidingView behavior="padding" style={{ flex: 1 }}>
      <Stack screenOptions={{ headerBackTitle: t('common.back') }}>
        <Stack.Screen name="login" options={{ headerShown: false }} />
        <Stack.Screen name="signup" options={{ headerShown: false }} />
        <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
        <Stack.Screen name="group/[id]" options={{ headerShown: false }} />
        <Stack.Screen name="group/create" options={{ headerShown: false, presentation: 'modal' }} />
        <Stack.Screen name="join" options={{ headerShown: false }} />
        <Stack.Screen name="trail/[id]" options={{ headerShown: false }} />
        <Stack.Screen name="trail/[id]/edit" options={{ headerShown: false }} />
      </Stack>

      {/* Feedback Modal */}
      <Modal visible={isFeedbackOpen} animationType="slide" transparent>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={{ marginBottom: 20 }}>{t('feedback.sendFeedback')}</Text>
            <TextInput
              value={feedbackText}
              onChangeText={setFeedbackText}
              placeholder={t('feedback.placeholder')}
              multiline
              style={styles.feedbackInput}
              editable={!isSubmittingFeedback}
            />
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
              <TouchableOpacity style={[styles.modalButton, { backgroundColor: '#ddd' }]} onPress={() => setIsFeedbackOpen(false)} disabled={isSubmittingFeedback}>
                <Text>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalButton, { backgroundColor: colors.backgroundPrimary }]} onPress={() => submitFeedback(feedbackText)} disabled={isSubmittingFeedback || !feedbackText.trim()}>
                {isSubmittingFeedback ? <ActivityIndicator color="#fff" /> : <Text style={{ color: '#fff' }}>{t('common.send')}</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Floating feedback button (hidden on record/follow to avoid covering controls) */}
      {!hideFab && (
        <TouchableOpacity
          style={styles.fab}
          onPress={() => setIsFeedbackOpen(true)}
          accessibilityLabel={t('feedback.sendFeedback')}
        >
          <MessageSquareQuote size={22} color={colors.backgroundPrimary} />
        </TouchableOpacity>
      )}
    </KBAvoidingView>
  );
}

export default function RootLayout() {
  return (
    <GestureHandlerRootView style={styles.container}>
      <KeyboardProvider>
        <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthContext>
            <DogsContext>
              <SelectedDogContext>
                <RootLayoutNav />
              </SelectedDogContext>
            </DogsContext>
          </AuthContext>
        </LanguageProvider>
        </QueryClientProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  fab: {
    position: 'absolute',
    right: 30,
    bottom: 120,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.accentPrimary,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.16,
    shadowRadius: 12,
    elevation: 8,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)'
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: 16,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  feedbackInput: {
    minHeight: 100,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 12,
    textAlignVertical: 'top'
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center'
  }
});